import {
  IAppointment,
  IAutomationScopeData,
  ICustomReport,
  IInvoice,
  IMarketingCampaign,
  IMarketingCampaignEntry,
  IMarketingCampaignRun,
  IPatient,
  IReportRun,
  ITreatmentPlan,
  ITreatmentStep,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  RefResolvable,
  resolveRef,
} from '@principle-theorem/shared';
import { Appointment } from './models/appointment/appointment';
import { TreatmentPlan } from './models/clinical-charting/treatment/treatment-plan';
import { TreatmentStep } from './models/clinical-charting/treatment/treatment-step';
import { ReportRun } from './models/custom-report/report-run';
import { Invoice } from './models/invoice/invoice';
import { MarketingCampaignEntry } from './models/marketing/campaign-entry';
import { MarketingCampaignRun } from './models/marketing/campaign-run';

export interface IAppLink {
  segments: string[];
  fragment?: string;
}

export class OrganisationLinks {
  static customReport(customReport: RefResolvable<ICustomReport>): IAppLink {
    const reportRef = resolveRef(customReport);
    return {
      segments: ['reporting', 'custom-reports', reportRef.id],
    };
  }

  static reportRunHistory(reportRun: RefResolvable<IReportRun>): IAppLink {
    const runRef = resolveRef(reportRun);
    const customReportId = ReportRun.customReportRef(asReffable(runRef)).id;
    return {
      segments: [
        'reporting',
        'custom-reports',
        customReportId ?? '',
        'history',
        runRef.id,
      ],
    };
  }
}

export class BrandLinks {
  static patient(patient: RefResolvable<IPatient>): IAppLink {
    const patientRef = resolveRef(patient);
    return {
      segments: ['patients', patientRef.id],
    };
  }

  static appointment(appointment: RefResolvable<IAppointment>): IAppLink {
    const appointmentRef = resolveRef(appointment);
    const patientRef = Appointment.patientRef(asReffable(appointmentRef));
    return {
      segments: ['patients', patientRef.id, 'appointment-history'],
      fragment: appointmentRef.id,
    };
  }

  static treatmentPlan(treatmentPlan: RefResolvable<ITreatmentPlan>): IAppLink {
    const treatmentPlanRef = resolveRef(treatmentPlan);
    const patientRef = TreatmentPlan.patientRef(asReffable(treatmentPlanRef));
    return {
      segments: [
        'patients',
        patientRef.id,
        'treatment-plans',
        treatmentPlanRef.id,
      ],
    };
  }

  static treatmentStep(treatmentStep: RefResolvable<ITreatmentStep>): IAppLink {
    const treatmentStepRef = resolveRef(treatmentStep);
    const treatmentPlanRef = TreatmentStep.treatmentPlanRef(
      asReffable(treatmentStepRef)
    );
    return BrandLinks.treatmentPlan(treatmentPlanRef);
  }

  static invoice(invoice: RefResolvable<IInvoice>): IAppLink {
    const invoiceRef = resolveRef(invoice);
    const patientRef = Invoice.patientRef(asReffable(invoiceRef));
    return {
      segments: [
        'patients',
        patientRef.id,
        'account',
        'invoices',
        invoiceRef.id,
      ],
    };
  }

  static marketingCampaign(
    marketingCampaign: RefResolvable<IMarketingCampaign>
  ): IAppLink {
    const campaignRef = resolveRef(marketingCampaign);
    return {
      segments: ['campaigns', campaignRef.id],
    };
  }

  static marketingCampaignEntry(
    marketingCampaignEntry: RefResolvable<IMarketingCampaignEntry>
  ): IAppLink {
    const entryRef = resolveRef(marketingCampaignEntry);
    const campaignRef = MarketingCampaignEntry.campaignRef(
      asReffable(entryRef)
    );
    return {
      segments: [
        ...BrandLinks.marketingCampaign(campaignRef).segments,
        'entries',
        entryRef.id,
      ],
    };
  }

  static marketingCampaignRun(
    marketingCampaignRun: RefResolvable<IMarketingCampaignRun>
  ): IAppLink {
    const runRef = resolveRef(marketingCampaignRun);
    const campaignRef = MarketingCampaignRun.campaignRef(asReffable(runRef));
    return {
      segments: [
        ...BrandLinks.marketingCampaign(campaignRef).segments,
        'runs',
        runRef.id,
      ],
    };
  }

  static automationScope(
    scopeData: IAutomationScopeData
  ): IAppLink | undefined {
    switch (scopeData.scope) {
      case TemplateScope.Patient:
        return BrandLinks.patient(scopeData.patientRef);
      case TemplateScope.Appointment:
        return BrandLinks.appointment(scopeData.appointmentRef);
      case TemplateScope.Invoice:
        return BrandLinks.invoice(scopeData.invoiceRef);
      case TemplateScope.TreatmentPlan:
        return BrandLinks.treatmentPlan(scopeData.treatmentPlanRef);
      case TemplateScope.TreatmentStep:
        return BrandLinks.treatmentStep(scopeData.treatmentStepRef);
      default:
        return;
    }
  }
}
